import { ReactNode } from "react";

import { External } from "components/icons/generated";
import { TIER_PLANS_LABELS } from "constants/tiers";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import Link from "ds/components/Link";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import { AnalyticsPageBilling } from "hooks/useAnalytics/pages/billing";
import { TypographyColors } from "types/Colors";
import { BillingTier } from "types/generated";

import BillingFeatureItem from "../../components/FeatureItem";
import BillingTierBadge from "../../components/TierBadge";
import { TALK_TO_SALES_LINK } from "../../constants";
import useBillingUpgradeOptionsContent from "../useContent";
import { getPreviousBillingTier } from "../utils";
import styles from "./styles.module.css";

type BillingUpgradeOptionsCardProps = {
  tier: BillingTier;
  title?: string;
  titleColor?: TypographyColors;
  price: string;
  description: string;
  action?: ReactNode;
  ctaButton?: ReactNode;
  upgradeBox?: ReactNode;
  className?: string;
};

const BillingUpgradeOptionsCardHorizontal = ({
  tier,
  title = "Starting from",
  titleColor = "secondary",
  price,
  description,
  action,
  ctaButton,
  upgradeBox,
  className,
}: BillingUpgradeOptionsCardProps) => {
  const { marketingTiers, detailsByTier, additionalFeaturesByTier } =
    useBillingUpgradeOptionsContent();

  const details = detailsByTier[tier];

  const previousTier = getPreviousBillingTier(tier, marketingTiers);

  const previousTierLabel = previousTier ? TIER_PLANS_LABELS[previousTier] : undefined;

  const ctaButtonTitle = "Talk to sales";

  return (
    <TileWrapper grid gridTemplate="1fr auto 1fr" gap="x-large" className={className}>
      <Box direction="column" gap="x-large" align="start">
        <Box justify="between" gap="large" fullWidth>
          <BillingTierBadge tier={tier} />
          {action}
        </Box>
        <Box direction="column" gap="medium">
          <Typography tag="p" variant="p-t8" color={titleColor}>
            {title}
          </Typography>
          <Typography tag="p" variant="p-t2">
            {price}
          </Typography>
          <Typography tag="p" variant="p-body4" color="secondary">
            {description}
          </Typography>
        </Box>

        <Box gap="medium">
          {details?.map(({ text, icon }, index) => (
            <BillingFeatureItem key={index} text={text} iconComponent={icon} />
          ))}
        </Box>
        {ctaButton || (
          <LinkButton
            variant="secondary"
            href={TALK_TO_SALES_LINK}
            target="_blank"
            size="small"
            analyticsPage={AnalyticsPageBilling.BillingDashboard}
            analyticsTitle="Upgrade Options CTA Button Clicked"
            analyticsProps={{
              ctaButtonTitle,
              plan: tier,
            }}
          >
            {ctaButtonTitle}
          </LinkButton>
        )}
      </Box>

      <hr className={styles.dividerVertical} />
      <Box direction="column" gap="x-large" align="start" fullWidth>
        <Box direction="column" gap="medium" fullWidth>
          <Box justify="between" align="center" gap="large" fullWidth>
            <Typography tag="p" variant="p-t7">
              All from {previousTierLabel} and
            </Typography>

            <Box gap="small">
              <Link
                target="_blank"
                href="https://spacelift.io/pricing"
                endIcon={External}
                analyticsPage={AnalyticsPageBilling.BillingDashboard}
                analyticsTitle="Upgrade Options - See All Link Clicked"
                analyticsProps={{
                  plan: tier,
                }}
              >
                <Typography tag="span" variant="p-body3">
                  See all
                </Typography>
              </Link>
            </Box>
          </Box>
          {additionalFeaturesByTier[tier]?.map((feature, index) => (
            <BillingFeatureItem key={index} text={feature.text} infoTooltip={feature.infoTooltip} />
          ))}
        </Box>
        {upgradeBox}
      </Box>
    </TileWrapper>
  );
};

export default BillingUpgradeOptionsCardHorizontal;
